import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const Home = () => {
  const APP_URL = "https://app.engagedent.ai";
  const HERO_IMAGE_URL = "https://media-manager-c.questera.ai/greta-media/8d9dfa490c69ce5d99a4069591f051bca5a96c8d3ad6ad18ff47bbad7a71bfd34566b681ef17ee0c8bc3f5644aa64256/images/aW1hZ2UvcG5n/9ea25b52660b88922948406971c8c8e1.png";

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 lg:pt-32 lg:pb-48 overflow-visible">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-cyan-50/50 rounded-bl-[100px] transform translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-blue-50/50 rounded-tr-[100px] transform -translate-x-1/4" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            
            {/* Left Content */}
            <motion.div 
              initial="hidden"
              animate="visible"
              variants={containerVariants}
              className="text-center lg:text-left z-20"
            >
              <motion.div 
                variants={itemVariants}
                className="inline-block mb-4 px-4 py-1.5 rounded-full bg-cyan-100 text-cyan-800 text-sm font-semibold tracking-wide"
              >
                🚀 The Future of Dental Marketing
              </motion.div>
              <motion.h1 
                variants={itemVariants}
                className="text-5xl lg:text-7xl font-extrabold tracking-tight text-slate-900 mb-6 leading-tight"
              >
                Your Digital <br />
                <span className="text-gradient">Dental Assistant</span>
              </motion.h1>
              <motion.p 
                variants={itemVariants}
                className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto lg:mx-0 leading-relaxed"
              >
                Generate scripts, create lifelike AI video avatars, and post directly to social media. Patient education and marketing, automated.
              </motion.p>
              <motion.div 
                variants={itemVariants}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <a 
                  href={APP_URL} 
                  className="px-8 py-4 bg-slate-900 text-white rounded-xl font-semibold shadow-lg shadow-slate-900/20 hover:bg-slate-800 hover:scale-105 transition-all text-center"
                >
                  Start for Free
                </a>
                <Link 
                  to="/demo" 
                  className="px-8 py-4 bg-white text-slate-700 border border-slate-200 rounded-xl font-semibold hover:bg-slate-50 hover:border-slate-300 transition-all text-center flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiIcons.FiPlayCircle} className="w-5 h-5" />
                  Watch Demo
                </Link>
              </motion.div>
            </motion.div>

            {/* Right Image Section */}
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border-8 border-white bg-slate-100 w-full">
                <img 
                  src={HERO_IMAGE_URL} 
                  alt="Dentist using EngageDent AI on tablet" 
                  className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-700"
                />
              </div>

              {/* Floating Elements - Positioned absolutely relative to the motion.div wrapper to stay visible */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ repeat: Infinity, duration: 4 }}
                className="absolute -top-10 -right-4 lg:-right-10 bg-white p-4 rounded-xl shadow-xl flex items-center gap-3 z-30 max-w-[200px]"
              >
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 flex-shrink-0">
                  <SafeIcon icon={FiIcons.FiCheck} />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-semibold">Script Ready</p>
                  <p className="text-sm font-bold text-slate-900 leading-tight">Patient Literacy</p>
                </div>
              </motion.div>

              <motion.div 
                animate={{ y: [0, 10, 0] }}
                transition={{ repeat: Infinity, duration: 5, delay: 1 }}
                className="absolute -bottom-8 -left-4 lg:-left-10 bg-white p-4 rounded-xl shadow-xl flex items-center gap-3 z-30 max-w-[220px]"
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 flex-shrink-0">
                  <SafeIcon icon={FiIcons.FiUploadCloud} />
                </div>
                <div>
                  <p className="text-xs text-slate-500 font-semibold">Posted to</p>
                  <p className="text-sm font-bold text-slate-900 leading-tight">Instagram & TikTok</p>
                </div>
              </motion.div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-10 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-slate-400 mb-6 uppercase tracking-wider">Seamlessly integrates with</p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
            <div className="flex items-center gap-2 text-xl font-bold text-slate-700"><SafeIcon icon={FiIcons.FiInstagram} /> Instagram</div>
            <div className="flex items-center gap-2 text-xl font-bold text-slate-700"><SafeIcon icon={FiIcons.FiFacebook} /> Facebook</div>
            <div className="flex items-center gap-2 text-xl font-bold text-slate-700"><SafeIcon icon={FiIcons.FiLinkedin} /> LinkedIn</div>
            <div className="flex items-center gap-2 text-xl font-bold text-slate-700"><SafeIcon icon={FiIcons.FiYoutube} /> YouTube</div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Create content in 3 simple steps</h2>
            <p className="text-lg text-slate-600">No filming, no distinct editing, no hassle.</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: FiIcons.FiType, 
                title: '1. Select Topic', 
                desc: 'Choose from patient literacy, testimonials, or treatments. Our AI writes the perfect script.' 
              },
              { 
                icon: FiIcons.FiUser, 
                title: '2. Generate Avatar', 
                desc: 'Select a professional digital avatar to deliver your message with human-like realism.' 
              },
              { 
                icon: FiIcons.FiShare2, 
                title: '3. Post Instantly', 
                desc: 'One-click publishing to all your social media channels in the correct formats.' 
              },
            ].map((step, idx) => (
              <div key={idx} className="bg-white p-8 rounded-2xl shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
                <div className="w-14 h-14 bg-cyan-100 rounded-xl flex items-center justify-center text-cyan-600 mb-6 text-2xl">
                  <SafeIcon icon={step.icon} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                <p className="text-slate-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Highlight - Studio Tier */}
      <section className="py-24 bg-slate-900 text-white overflow-hidden relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-500/20 blur-[100px] rounded-full pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm font-semibold mb-6 border border-cyan-500/30">
                New Feature
              </div>
              <h2 className="text-3xl md:text-5xl font-bold mb-6">Unleash creativity with <br/><span className="text-cyan-400">Studio Mode</span></h2>
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                Take your content to the next level. Mix your AI avatar with custom B-roll, AI-generated visuals, and practice images for a fully customized production.
              </p>
              
              <ul className="space-y-4 mb-8">
                {[
                  'Upload your own clinic B-roll footage',
                  'Generate AI visuals to match your script',
                  'Advanced timeline editing capabilities',
                  'Custom branding and watermarks'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-300">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="text-cyan-400 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
              
              <Link to="/pricing" className="inline-flex items-center gap-2 text-cyan-400 font-semibold hover:text-cyan-300 transition-colors">
                Explore Studio Tier <SafeIcon icon={FiIcons.FiArrowRight} />
              </Link>
            </div>
            <div className="relative">
              <div className="aspect-square bg-slate-800 rounded-3xl border border-slate-700 p-4 shadow-2xl rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="h-full w-full bg-slate-900 rounded-2xl flex flex-col items-center justify-center text-slate-500">
                  <SafeIcon icon={FiIcons.FiVideo} className="text-6xl mb-4 text-cyan-500/50" />
                  <p>Studio Editor Interface</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-white text-center">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-slate-900 mb-6">Ready to engage your patients?</h2>
          <p className="text-xl text-slate-600 mb-10">Join hundreds of dentists using AI to grow their practice online.</p>
          <a href={APP_URL}>
            <button className="bg-cyan-500 hover:bg-cyan-600 text-white text-lg px-10 py-4 rounded-full font-bold shadow-lg shadow-cyan-500/30 transition-all transform hover:-translate-y-1">
              Get Started for Free
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Home;